// ─── OCNexus API Client ─────────────────────────────────────────
// Typed fetch wrappers for the OCNexus REST API.
// Base URL is configured via VITE_API_URL env var.
// All requests include the Supabase session JWT for auth.
//
// AUTH STRATEGY: Reads the cached session from AuthContext (populated by
// onAuthStateChange) instead of calling supabase.auth.getSession() which
// can hang forever due to internal initialization promise deadlocks.
//
// 401 HANDLING: A failed Supabase refresh emits a 'session-expired' event
// via sessionExpiredBus. A 401 that persists after a successful refresh is
// authorization failure and is returned to the caller.
import { supabase } from './lib/supabaseClient';
import { getCachedSession } from './context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

// ─── Stream Token Helper ────────────────────────────────────────
// Requests a short-lived (5m) JWT that auths SSE handshakes.
// Returns null on failure. Protected stream callers must fail closed and use
// their existing status/polling recovery path rather than opening a bare URL.
export async function fetchStreamToken(
  resourceType: 'pipeline_run' | 'chat_message' | 'guardian_run' | 'sync_run' | 'chat_session' | 'factory_build',
  resourceId: string,
  accessToken: string,
): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/api/stream-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        resource_type: resourceType,
        resource_id: resourceId,
      }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { stream_token?: string };
    return body.stream_token ?? null;
  } catch {
    return null;
  }
}

/**
 * Builds a protected SSE URL only when a server-minted stream capability is
 * available. Returning null makes it impossible for a caller to accidentally
 * downgrade to a bare resource stream when strict stream-token auth is live.
 */
export function buildProtectedStreamUrl(baseUrl: string, streamPath: string, streamToken: string | null | undefined): string | null {
  if (!streamToken) return null;
  return `${baseUrl}${streamPath}?token=${encodeURIComponent(streamToken)}`;
}

// ─── Session Expired Event Bus ──────────────────────────────────
// Simple pub/sub so api.ts (no React) can notify AuthContext (React).

type SessionExpiredListener = () => void;
const _expiredListeners = new Set<SessionExpiredListener>();
let _sessionExpired = false;

export const sessionExpiredBus = {
  /** Subscribe to session-expired events. Returns unsubscribe function. */
  on(fn: SessionExpiredListener): () => void {
    _expiredListeners.add(fn);
    return () => { _expiredListeners.delete(fn); };
  },
  /** Emit session-expired — called once when session refresh fails. */
  emit(): void {
    if (_sessionExpired) return; // Only fire once
    _sessionExpired = true;
    _expiredListeners.forEach((fn) => fn());
  },
  /** Reset after re-authentication. */
  reset(): void {
    _sessionExpired = false;
  },
  /** True if session refresh has failed and the event has not been reset. */
  get expired(): boolean {
    return _sessionExpired;
  },
};

/** Get the current Supabase access token for API auth headers.
 *  The AuthContext cache is populated asynchronously after first paint. On a
 *  cache miss, wait briefly for that event before consulting Supabase directly.
 *  Every Supabase operation is bounded: SDK initialization can otherwise leave
 *  getSession() (or refreshSession()) pending indefinitely. */
export async function authHeaders(): Promise<Record<string, string>> {
  // 1. The normal path is synchronous and does not touch the Supabase SDK.
  let cached = getCachedSession();
  if (cached?.access_token) {
    return { Authorization: `Bearer ${cached.access_token}` };
  }

  // 2. Bridge the initial auth-hydration window. In particular, do not send an
  //    anonymous request merely because onAuthStateChange is a few ticks late.
  for (let attempt = 0; attempt < 30; attempt += 1) {
    await new Promise<void>((resolve) => setTimeout(resolve, 100));
    cached = getCachedSession();
    if (cached?.access_token) {
      return { Authorization: `Bearer ${cached.access_token}` };
    }
  }

  // 3. Retain the bounded getSession fallback for caches not managed by the
  //    mounted AuthProvider (for example, an early consumer during bootstrap).
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 3_000)),
    ]);
    if (result && result.data?.session?.access_token) {
      return { Authorization: `Bearer ${result.data.session.access_token}` };
    }
  } catch (err) {
    console.error('[authHeaders] fallback getSession failed:', err instanceof Error ? err.message : err);
  }

  // 4. A stale/empty SDK session can still be recovered with a bounded refresh.
  const refreshed = await refreshAndGetHeaders();
  if (refreshed.Authorization) return refreshed;

  console.warn('[authHeaders] No session available');
  return {};
}

/** Force-refresh the Supabase session and return fresh auth headers.
 *  Used on 401 retry. 5s timeout since if refresh hangs, the session is dead.
 *
 *  SINGLE-FLIGHT: concurrent 401s (a burst of dashboard requests on load) must
 *  share ONE refreshSession() call. Parallel refreshes race on Supabase's
 *  single-use refresh token — and each gets its own 5s timeout, so a
 *  slow-but-valid refresh times out for some callers, returns {}, and used to
 *  emit a STICKY app-wide "Session expired" that bounced a perfectly good
 *  session right after login. Deduping to one in-flight refresh removes both. */
let _refreshInFlight: Promise<Record<string, string>> | null = null;

export async function refreshAndGetHeaders(): Promise<Record<string, string>> {
  // Reuse the in-flight refresh if one is already running; otherwise start one.
  // The explicit Promise<Record<string,string>> annotation keeps the `return {}`
  // branch from narrowing Authorization to `undefined`, and the `??` keeps the
  // local non-null so the awaited value is never `Record | null`.
  const inFlight: Promise<Record<string, string>> = _refreshInFlight ?? (
    _refreshInFlight = (async (): Promise<Record<string, string>> => {
      try {
        const result = await Promise.race([
          supabase.auth.refreshSession(),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 5_000)),
        ]);
        if (result && 'data' in result && result.data?.session?.access_token) {
          return { Authorization: `Bearer ${result.data.session.access_token}` };
        }
      } catch (err) {
        console.error('[refreshAndGetHeaders]', err instanceof Error ? err.message : err);
      }
      return {};
    })()
  );
  try {
    return await inFlight;
  } finally {
    if (_refreshInFlight === inFlight) _refreshInFlight = null;
  }
}

/**
 * Core fetch wrapper with automatic 401 retry.
 * On a 401 response, refreshes the Supabase session and retries ONCE.
 * A failed refresh emits session-expired; a persistent 401 after a successful
 * refresh is returned to the caller as an authorization failure.
 */
export async function apiFetch(url: string, init: RequestInit): Promise<Response> {
  // Short-circuit all requests once session is known expired
  if (sessionExpiredBus.expired) {
    throw new Error('Session expired — please sign in again');
  }

  const res = await fetch(url, init);
  if (res.status === 401) {
    // Refresh session and retry once
    const freshHeaders = await refreshAndGetHeaders();
    if (freshHeaders.Authorization) {
      const retryInit = {
        ...init,
        headers: { ...init.headers as Record<string, string>, ...freshHeaders },
      };
      const retryRes = await fetch(url, retryInit);
      // A retry 401 means this endpoint is not authorized for an otherwise
      // valid session. Return it so the caller can apply its local fallback.
      return retryRes;
    }
    // Refresh came back empty. Do NOT immediately declare the session dead — a
    // single transient refresh blip (network/timeout/SDK refresh-lock) must not
    // trigger the STICKY app-wide "Session expired" bounce. Give it one more
    // bounded attempt, then fall back to a still-valid cached token before emit.
    await new Promise((resolve) => setTimeout(resolve, 800));
    const retryHeaders = await refreshAndGetHeaders();
    if (retryHeaders.Authorization) {
      return await fetch(url, {
        ...init,
        headers: { ...init.headers as Record<string, string>, ...retryHeaders },
      });
    }
    // Still no fresh token. If the cached session still holds a non-expired
    // access token, this 401 was not a dead session — return it to the caller
    // instead of nuking the whole app to the login screen.
    const cached = getCachedSession();
    const expMs = cached?.expires_at ? cached.expires_at * 1000 : 0;
    if (cached?.access_token && expMs > Date.now() + 5_000) {
      return res;
    }
    // Genuinely could not refresh and no valid cached token — session is dead.
    sessionExpiredBus.emit();
  }
  return res;
}

async function get<T>(path: string): Promise<T> {
  let headers = await authHeaders();
  if (!headers.Authorization) {
    headers = await refreshAndGetHeaders();
    if (!headers.Authorization) throw new Error('Session expired — please sign in again');
  }
  const res = await apiFetch(`${API_BASE}/api${path}`, { headers });
  // apiFetch owns the bounded refresh-and-replay policy. Do not replay here:
  // callers must observe a persistent 401 after one canonical retry.
  if (!res.ok) {
    if (res.status === 401) throw new Error('Session expired — please sign in again');
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `API error: ${res.status}`);
  }
  return res.json();
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  let auth = await authHeaders();
  if (!auth.Authorization) {
    auth = await refreshAndGetHeaders();
    if (!auth.Authorization) throw new Error('Session expired — please sign in again');
  }
  const res = await apiFetch(`${API_BASE}/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: body ? JSON.stringify(body) : undefined,
  });
  // apiFetch owns the bounded refresh-and-replay policy. Do not replay here:
  // callers must observe a persistent 401 after one canonical retry.
  if (!res.ok) {
    if (res.status === 401) throw new Error('Session expired — please sign in again');
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(data.message || data.error || `API error: ${res.status}`);
  }
  return res.json();
}

/** POST that returns a ReadableStream for SSE */
async function postStream(path: string, body?: unknown): Promise<Response> {
  const auth = await authHeaders();
  const res = await apiFetch(`${API_BASE}/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res;
}

async function del<T>(path: string): Promise<T> {
  const auth = await authHeaders();
  const res = await apiFetch(`${API_BASE}/api${path}`, { method: 'DELETE', headers: auth });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(data.message || data.error || `API error: ${res.status}`);
  }
  return res.json();
}

async function patch<T>(path: string, body?: unknown): Promise<T> {
  const auth = await authHeaders();
  const res = await apiFetch(`${API_BASE}/api${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(data.message || data.error || `API error: ${res.status}`);
  }
  return res.json();
}
async function put<T>(path: string, body?: unknown): Promise<T> {
  const auth = await authHeaders();
  const res = await apiFetch(`${API_BASE}/api${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(data.message || data.error || `API error: ${res.status}`);
  }
  return res.json();
}

// ─── Types ──────────────────────────────────────────────────────────
export interface PendingAction {
  id: string;
  project_id: string;
  project_name: string;
  type: 'human_gate' | 'sync_review' | 'sync_failed' | 'waiting_for_input';
  title: string;
  description: string;
  pr_number?: number;
  run_id?: string;
  sync_run_id?: string;
  questions?: string[];
  repo?: string;
  created_at: string;
  _group_ids?: string[];
}

export interface HealthResponse {
  status: string;
  version: string;
  projects: number;
  timestamp: string;
}

export interface ProjectSummary {
  project_id: string;
  display_name: string;
  repo: string;
  packages: number;
  source?: 'registry' | 'wizard';
  wizard_complete?: boolean;
  wizard_state?: WizardStateFromDB | null;
  onboarding_state?: string;
  onboarding_error?: string | null;
  repo_owner?: string | null;
  repo_name?: string | null;
}

/** Wizard state as stored in DB — used for post-wizard Overview panel */
export interface WizardStateFromDB {
  steps: Record<string, string>;
  unlockedCapabilities: string[];
  automationLog: string[];
  codexStatus?: string;
  specMd?: string;
}

export interface ProjectDetail {
  project_id: string;
  display_name: string;
  description?: string;
  repo: { owner: string; name: string; default_branch: string; monorepo?: boolean };
  packages: Array<{
    name: string;
    path: string;
    type?: string;
    test_command?: string;
    build_command?: string;
    framework?: string;
  }>;
  ai?: Record<string, unknown>;
  routing?: { rules: Array<{ match: string; route: string; reason?: string }> };
  notifications?: Record<string, unknown>;
  security_baseline_at?: string | null;
  security_findings?: SecurityFinding[] | null;
  security_score?: number | null;
  wizard_state?: WizardStateFromDB | null;
  wizard_complete?: boolean;
  /** ocn-S790-P0: non-null when the subscriber has archived (soft-hidden) this project. */
  subscriber_hidden_at?: string | null;
}

export interface SecurityFinding {
  severity: string;
  title: string;
  file?: string;
  evidence?: string;
  impact?: string;
  fix?: string;
  test?: string;
}

export interface PipelineRun {
  id: string;
  project_id: string;
  issue_number?: number;
  issue_text?: string;
  input_text?: string;
  trigger: string;
  route?: string;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  cost?: number;
  pr_number?: number;
  branch?: string;
  branch_name?: string;
  error?: string;
}

export interface CostSummary {
  total: number;
  by_engine: Record<string, number>;
  by_day: Array<{ date: string; cost: number }>;
}

export interface Baseline {
  project_id: string;
  created_at: string;
  test_count: number;
  pass_count: number;
  fail_count: number;
  coverage?: number;
}

export interface TestStatus {
  running: boolean;
  last_result: {
    status: string;
    completed_at: string;
    duration_ms: number;
    summary: { files: number; suites: number; tests: number; passed: number; failed: number; skipped: number };
  } | null;
}

// ─── Chat Types ─────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  project_id?: string;
  metadata?: {
    model?: string;
    tokens_used?: number;
    cost?: number;
    action_taken?: string;
    run_id?: string;
    chat_run_id?: string;
    chatRunId?: string;
    [key: string]: unknown;
  };
  sync_run_id?: string;
  phase?: string;
  suggestions?: Array<{ label: string; prompt: string }>;
}

export interface ChatSession {
  id: string;
  project_id?: string;
  created_at: string;
  last_message_at: string;
  message_count: number;
  preview: string;
}

/// ─── Sync Types ───────────────────────────────────────────

export interface SyncPhase {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  error?: string;
  output?: Record<string, unknown>;
}

export interface SyncRun {
  id: string;
  project_id: string;
  status: 'running' | 'review_required' | 'audit_pending' | 'approved' | 'completed' | 'failed' | 'cancelled';
  current_phase: string;
  triggered_by: string;
  review_required: boolean;
  agents_md_diff?: unknown;
  routing_rules_diff?: unknown;
  packages_diff?: unknown;
  phases: SyncPhase[];
  started_at: string;
  completed_at?: string;
  created_at?: string;
  duration_ms?: number | null;
}


export interface DebtMetric {
  id: string;
  sync_run_id?: string;
  any_count: number;
  empty_catch_count: number;
  god_file_count: number;
  largest_file_lines: number;
  sast_p0_count: number;
  sast_p1_count: number;
  eslint_error_count: number;
  eslint_warning_count: number;
  test_count: number;
  created_at: string;
}

// ─── Logs Types ─────────────────────────────────────────────────

export interface RunDetail extends PipelineRun {
  steps: RunStep[];
  llm_calls: LLMCall[];
  files_changed: FileChange[];
}

export interface RunStep {
  id: string;
  name: string;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  output?: string;
  error?: string;
}

export interface LLMCall {
  id: string;
  engine: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  cost: number;
  duration_ms: number;
  timestamp: string;
}

export interface FileChange {
  path: string;
  action: 'added' | 'modified' | 'deleted';
  additions: number;
  deletions: number;
}

export interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  source?: string;
}

// ─── GitHub Types ───────────────────────────────────────────────

export interface GitHubRepo {
  name: string;
  full_name: string;
  description: string | null;
  language: string | null;
  private: boolean;
  default_branch: string;
  updated_at: string;
  html_url: string;
}

export interface GuardianRunRecord {
  id: string;
  project_id: string;
  trigger: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  regressions_json: unknown[];
  fixed_json: unknown[];
  unfixable_json: unknown[];
  auto_merged: boolean;
  cost_usd: number;
}

// ─── API Functions ──────────────────────────────────────────────

// ─── Pricing (ocn-S367 · §C/§B build pricing surfaces) ──────────────────────
/** Role-aware buildout cost preview. Operator sees the full margin; subscriber only the charge. */
export type BuildCostPreview =
  | { role: 'operator'; rawComputeCostUsd: number; markupMultiplier: number; marginUsd: number; customerChargeUsd: number; fireCount: number; capUsd: number | null; remainingUsd: number | null; meterSource: string }
  | { role: 'subscriber'; display: true; customerChargeUsd: number; capUsd: number | null; remainingUsd: number | null }
  | { role: 'subscriber'; display: false; capUsd: number | null }
  | { role: 'operator' | 'subscriber'; unavailable: true; capUsd: number | null };

export interface FoundationQuoteLineItem { key: string; category: string; valueUsd: number; }
export interface BuildFoundationQuote {
  totalUsd: number;
  componentCount: number;
  lineItems: FoundationQuoteLineItem[];
  byCategory: Record<string, number>;
  unknownComponents: string[];
  uncategorizedComponents: string[];
  /** 'corpus' = grounded in the real skills DB; 'heuristic' = keyword fallback. */
  source: 'corpus' | 'heuristic';
  /** Real skill keys the build's components matched in the corpus. */
  matchedSkills: string[];
  provisional: true;
}
export interface BuildInvoice { foundationUsd: number; buildoutUsd: number; totalUsd: number; }
export interface BuildFoundationQuoteResponse { foundation: BuildFoundationQuote; invoice: BuildInvoice; }

export interface SubscriberMcpAccess {
  account_created: true;
  provisioning_status: 'provisioning' | 'ready' | 'blocked';
  readiness_checked_at: string | null;
  /** True once this subscriber has approved an assistant (Grok Bot) connection. */
  assistant_connected?: boolean;
  assistant_connected_at?: string | null;
}

export const getSubscriberMcpAccess = (): Promise<SubscriberMcpAccess> => get<SubscriberMcpAccess>('/subscriber/mcp-access');

export interface SubscriberRepos { repos: string[]; installed: boolean }
export const getSubscriberRepos = (): Promise<SubscriberRepos> => get<SubscriberRepos>('/subscriber/repos');
export interface SelectSubscriberRepoResult { repo: string; provisioning_status: SubscriberMcpAccess['provisioning_status']; action: string; reason?: string | null }
export const selectSubscriberRepo = (repo: string): Promise<SelectSubscriberRepoResult> => post<SelectSubscriberRepoResult>('/subscriber/repo', { repo });
export const getGithubInstallUrl = (): Promise<{ url: string }> => get<{ url: string }>('/github-app-install-capture/install-url');
/** Authenticated, project-bound signed installation flow; distinct from account capture. */
export const getProjectGithubInstallUrl = (projectId: string): Promise<{ url: string }> =>
  get<{ url: string }>('/github-app/install?' + new URLSearchParams({ projectId, format: 'json' }).toString());

export const api = {
  getSubscriberChats: (projectId: string, includeArchived = false) => get<{ archiveAvailable?: boolean; chats: Array<{ sessionId: string; chatSessionId: string; title: string; archived_at?: string | null; archive_updated_at?: string | null }> }>(`/subscriber-chat/chats?projectId=${encodeURIComponent(projectId)}${includeArchived ? '&includeArchived=true' : ''}`),
  createSubscriberChat: (projectId: string) => post<{ chatSessionId: string }>('/subscriber-chat/chats', { projectId }),
  createChatProject: (name: string) => post<{ projectId: string; sessionId: string; chatSessionId: string }>('/subscriber-chat/projects', { name }),
  renameSubscriberChat: (sessionId: string, title: string) => patch<{ id: string; title: string }>(`/subscriber-chat/chats/${encodeURIComponent(sessionId)}`, { title }),
  archiveSubscriberChat: (sessionId: string, archived: boolean, expectedUpdatedAt: string | null) => post(`/subscriber-chat/chats/${encodeURIComponent(sessionId)}/archive`, { archived,expectedUpdatedAt }),
  moveSubscriberChat: (sessionId:string,input:{projectId:string;expectedProjectId:string;expectedChatUpdatedAt:string;expectedWrapperUpdatedAt:string}) => post(`/subscriber-chat/chats/${encodeURIComponent(sessionId)}/move`,input),
  associateChatRepository: (sessionId: string, repositoryId: string) => post(`/subscriber-chat/chats/${encodeURIComponent(sessionId)}/repositories`, { repositoryId }),
  archiveChatRepository: (sessionId: string, repositoryId: string, archived: boolean, expectedUpdatedAt: string) => patch(`/subscriber-chat/chats/${encodeURIComponent(sessionId)}/repositories/${encodeURIComponent(repositoryId)}`, { archived, expectedUpdatedAt }),
  /** GET /health (root-level) */
  health: () => get<HealthResponse>('/health'),

  /** GET /api/projects */
  listProjects: () => get<{ projects: ProjectSummary[] }>('/projects').then(r => r.projects),

  /** PATCH /api/projects/:id — rename a project */
  renameProject: (id: string, name: string) => patch<{ id: string; name: string }>(`/projects/${encodeURIComponent(id)}`, { name }),

  /** POST /api/projects/:id/archive — soft-hide from the subscriber project list (reversible) */
  archiveProject: (id: string) => post<{ status: string; id: string }>(`/projects/${encodeURIComponent(id)}/archive`),

  /** POST /api/projects/:id/unarchive — restore a soft-hidden project */
  unarchiveProject: (id: string) => post<{ status: string; id: string }>(`/projects/${encodeURIComponent(id)}/unarchive`),

  /** GET /api/projects/:id
   * Two API handlers serve this path with different shapes: the legacy
   * projects route returns `{ project: {...} }`, while projects-chats.ts
   * returns the project flat/unwrapped. Tolerate both so a flat response
   * doesn't render as "Project not found" (r.project would be undefined). */
  getProject: (id: string) =>
    get<Record<string, unknown>>(`/projects/${id}`).then((r) => {
      const p = (r && typeof r === 'object' && 'project' in r ? r.project : r) as Record<string, unknown>;
      // Normalize: the lean projects-chats shape omits `packages` and may carry a
      // partial/flat `repo` (repo_owner/repo_name). ProjectDetailPage assumes the
      // rich shape, so guarantee `packages` is an array and `repo` is an object —
      // otherwise `project.packages.length` / `project.repo.owner` crash the page.
      const repoObj = (p.repo && typeof p.repo === 'object')
        ? p.repo as Record<string, unknown>
        : { owner: p.repo_owner ?? '', name: p.repo_name ?? '', default_branch: p.default_branch ?? 'main' };
      return {
        ...p,
        packages: Array.isArray(p.packages) ? p.packages : [],
        repo: repoObj,
      } as unknown as ProjectDetail;
    }),

  /** GET /api/runs?project_id=X&limit=N */
  listRuns: (projectId: string, limit = 50) =>
    get<{ runs: PipelineRun[] }>(`/runs?project_id=${projectId}&limit=${limit}`).then(r => r.runs),

  /** GET /api/runs/:id */
  getRun: (id: string) => get<{ run: PipelineRun }>(`/runs/${id}`).then(r => r.run),

  /** GET /api/runs/:id/calls */
  getRunCalls: (id: string) => get<{ calls: LLMCall[] }>(`/runs/${id}/calls`).then(r => r.calls),

  /** GET /api/runs/:id/steps */
  getRunSteps: (id: string) => get<{ steps: RunStep[] }>(`/runs/${id}/steps`).then(r => r.steps).catch(() => []),

  /** GET /api/runs/:id/files */
  getRunFiles: (id: string) => get<{ files: FileChange[] }>(`/runs/${id}/files`).then(r => r.files).catch(() => []),

  /** GET /api/runs/:id/logs */
  getRunLogs: (id: string) => get<{ logs: LogEntry[] }>(`/runs/${id}/logs`).then(r => r.logs).catch(() => []),

  /** POST /api/runs/:id/dismiss — soft-delete a pipeline run */
  dismissRun: (id: string) => post<{ status: string }>(`/runs/${id}/dismiss`, {}),

  /** POST /api/runs/dismiss-all — dismiss all failed runs for a project */
  dismissAllRuns: (projectId: string) =>
    post<{ status: string; count: number }>('/runs/dismiss-all', { project_id: projectId }),

  /** GET /api/costs/:projectId?days=N */
  getCosts: (projectId: string, days = 30) => get<CostSummary>(`/costs/${projectId}?days=${days}`),

  /** GET /api/factory-builds/:id/cost-preview — role-aware buildout cost. */
  getBuildCostPreview: (buildId: string) =>
    get<BuildCostPreview>(`/factory-builds/${buildId}/cost-preview`),
  /** PATCH /api/factory-builds/:id/spend-cap — set (or clear with null) the customer spend cap. */
  setBuildSpendCap: (buildId: string, spend_cap_usd: number | null) =>
    patch<{ ok: boolean; capUsd: number | null }>(`/factory-builds/${buildId}/spend-cap`, { spend_cap_usd }),
  /** GET /api/factory-builds/:id/foundation-quote — §B substrate-value quote + invoice. */
  getBuildFoundationQuote: (buildId: string) =>
    get<BuildFoundationQuoteResponse>(`/factory-builds/${buildId}/foundation-quote`),

  /** GET /api/baselines/:projectId */
  getBaseline: (projectId: string) => get<{ baseline: Baseline }>(`/baselines/${projectId}`).then(r => r.baseline),

  /** POST /api/dispatch — returns either pipeline or onboarding sync response */
  dispatch: (projectId: string, issueText: string, issueNumber?: number, repoOwner?: string, repoName?: string) =>
    post<{ status: string; message: string; sync_run_id?: string; stream_url?: string; phases?: SyncPhase[]; idempotent?: boolean }>('/dispatch', { project_id: projectId, issue_text: issueText, issue_number: issueNumber, repo_owner: repoOwner, repo_name: repoName }),

  /** POST /api/guardian/:projectId — trigger manual run */
  triggerGuardian: (projectId: string) =>
    post<{ status: string; run_id: string; stream_url: string }>(`/guardian/${projectId}`),

  /** GET /api/guardian/status/:projectId — latest run status for button state */
  guardianStatus: (projectId: string) =>
    get<{
      status: string;
      last_run: GuardianRunRecord | null;
      stream_url?: string;
    }>(`/guardian/status/${projectId}`),

  /** GET /api/guardian/runs/:projectId — recent guardian runs */
  guardianRuns: (projectId: string, limit = 20) =>
    get<{ runs: GuardianRunRecord[] }>(`/guardian/runs/${projectId}?limit=${limit}`).then(r => r.runs),

  /** POST /api/test */
  runTests: (opts?: { timeout_ms?: number; include_raw_output?: boolean }) =>
    post<unknown>('/test', opts),

  /** GET /api/test/status */
  testStatus: () => get<TestStatus>('/test/status'),

  /** GET /api/test/last */
  testLast: () => get<unknown>('/test/last'),

  // ─── Chat ──────────────────────────────────────────────────────

  /** POST /api/chat — send a message, get streamed response */
  chatSend: (message: string, projectId?: string, sessionId?: string) =>
    postStream('/chat', { message, project_id: projectId, session_id: sessionId }),

  /** GET /api/chat/sessions */
  chatSessions: () => get<{ sessions: ChatSession[] }>('/chat/sessions').then(r => r.sessions).catch(() => []),

  /** GET /api/chat/sessions/:id */
  chatHistory: (sessionId: string) =>
    get<{ messages: ChatMessage[] }>(`/chat/sessions/${sessionId}`).then(r => r.messages).catch(() => []),

  /** DELETE /api/chat/sessions/:id */
  chatDeleteSession: async (sessionId: string) => {
    const auth = await authHeaders();
    const res = await fetch(`${API_BASE}/api/chat/sessions/${sessionId}`, { method: 'DELETE', headers: auth });
    if (!res.ok) throw new Error('Failed to delete session');
    return res.json();
  },

  // ─── Sync ───────────────────────────────────────────────────

  /** POST /api/sync/run — start a new sync */
  syncRun: (projectId: string, triggeredBy?: string) =>
    post<{ run_id: string; status: string; stream_url: string; phases: SyncPhase[]; estimate?: { minutes: number; label: string; file_count: number } }>('/sync/run', { project_id: projectId, triggered_by: triggeredBy }),

  /** GET /api/sync/status/:projectId */
  syncStatus: (projectId: string) =>
    get<{ status: string; last_run: SyncRun | null; stream_url?: string; last_completed_at?: string | null }>(`/sync/status/${projectId}`),

  /** GET /api/sync/review/:runId */
  syncReview: (runId: string) =>
    get<{ run_id: string; project_id: string; agents_md_diff: unknown; routing_rules_diff: unknown; packages_diff: unknown; phases: SyncPhase[] }>(`/sync/review/${runId}`),

  /** POST /api/sync/approve/:runId */
  syncApprove: (runId: string, approved: boolean, skip?: string[]) =>
    post<{ status: string }>(`/sync/approve/${runId}`, { approved, skip }),

  /** GET /api/sync/history/:projectId */
  syncHistory: (projectId: string, limit?: number) =>
    get<{ runs: SyncRun[] }>(`/sync/history/${projectId}${limit ? `?limit=${limit}` : ''}`).then(r => r.runs).catch(() => []),

  /** DELETE /api/sync/history/:projectId — clear sync history, keep latest */
  clearSyncHistory: (projectId: string, keep = 1) =>
    del<{ deleted: number; message: string }>(`/sync/history/${projectId}?keep=${keep}`),

  /** GET /api/sync/debt-trend/:projectId */
  debtTrend: (projectId: string, limit = 10) =>
    get<{ metrics: DebtMetric[] }>(`/sync/debt-trend/${projectId}?limit=${limit}`).then(r => r.metrics).catch(() => []),

  /** GET /api/sync/audit-estimate/:projectId — provider options + credit balance */
  syncAuditEstimate: (projectId: string) =>
    get<{ providers: Array<{ id: string; name: string; label: string; creditRange: [number, number]; timeRangeMinutes: [number, number]; available: boolean; reason?: string }>; balance: number | null; balance_unavailable_reason?: string }>(`/sync/audit-estimate/${projectId}`),

  /** POST /api/sync/audit-approve/:runId — approve or skip architectural review */
  syncAuditApprove: (runId: string, approved: boolean) =>
    post<{ status: string }>(`/sync/audit-approve/${runId}`, { approved }),

  /** GET /api/sync/review-comparison/:runId — multi-provider comparison results */
  syncReviewComparison: (runId: string) =>
    get<{ comparisons: Array<{ provider: string; status: string; findings_json: unknown; architecture_summary: string | null; tech_debt_score: number | null; tokens_used: number | null; cost_usd: number | null; duration_ms: number | null; p0_count: number | null; p1_count: number | null; p2_count: number | null }> }>(`/sync/review-comparison/${runId}`).then(r => r.comparisons).catch(() => []),

  /** GET /api/sync/:runId/gate-state — polling-based review gate state (ocn-S16-P0) */
  syncGateState: (runId: string) =>
    get<{
      syncId: string;
      status: 'running' | 'review_required' | 'audit_pending' | 'completed' | 'failed' | 'cancelled';
      state: 'idle' | 'syncing' | 'audit_pending' | 'review_running' | 'review_complete' | 'error';
      auditCreatedAt: string | null;
      reviewStartedAt: string | null;
      reviewCompletedAt: string | null;
      reviewSummary: { findingsAdded: number; techDebtScore: number | null; summary: string | null } | null;
      errorMessage: string | null;
    }>(`/sync/${runId}/gate-state`),

  /** POST /api/manus/audit/:projectId/trigger — kick off a Manus deep review */
  manusAuditTrigger: (projectId: string) =>
    post<{ taskId?: string; findingsCount?: number; techDebtScore?: number; architectureSummary?: string; riskAreas?: unknown }>(`/manus/audit/${projectId}/trigger`, {}),

  // ─── GitHub ─────────────────────────────────────────────

  /** GET /api/github/diff/:prNumber?project_id=xxx */
  githubDiff: (prNumber: number, projectId: string) =>
    get<{ pr_number: number; files: Array<{ filename: string; status: string; additions: number; deletions: number; patch: string | null }>; diff: string; total_files: number; total_additions: number; total_deletions: number }>(
      `/github/diff/${prNumber}?project_id=${encodeURIComponent(projectId)}`
    ),

  /** POST /api/chat/approve/:runId */
  chatApprove: (runId: string, action: 'approve' | 'reject', reason?: string) =>
    post<{ status: string }>(`/chat/approve/${runId}`, { action, reason }),

  /** POST /api/chat/proceed/:runId — resume a paused pipeline (cost confirmation gate) */
  chatProceed: (runId: string) =>
    post<{ ok: boolean; run_id: string }>(`/chat/proceed/${runId}`),

  /** DELETE /api/chat/run/:runId — cancel a pipeline run */
  chatCancelRun: (runId: string) =>
    del<{ ok: boolean; run_id: string }>(`/chat/run/${runId}`),

  /** POST /api/chat/answer/:runId */
  chatAnswer: (runId: string, answers: string[]) =>
    post<{ status: string }>(`/chat/answer/${runId}`, { answers }),

  // ─── Actions ────────────────────────────────────────────────────

  /** GET /api/actions/pending */
  getPendingActions: () =>
    get<{ actions: PendingAction[] }>('/actions/pending').then(r => r.actions).catch(() => []),

  /** POST /api/actions/dismiss/:runId — soft-delete a single action */
  dismissAction: (runId: string) =>
    post<{ status: string }>(`/actions/dismiss/${runId}`, {}),

  /** POST /api/actions/dismiss-all — dismiss all failed sync actions */
  dismissAllActions: () =>
    post<{ status: string; count: number }>('/actions/dismiss-all', {}),

  // ─── GitHub ─────────────────────────────────────────────────────

  /** GET /api/github/repos?org=X */
  listGitHubRepos: (org = 'lumeniqjs') =>
    get<{ repos: GitHubRepo[] }>(`/github/repos?org=${encodeURIComponent(org)}`).then(r => r.repos),

  /** POST /api/github/repos — create a new repo (private by default) */
  createGitHubRepo: (name: string, options?: { org?: string; description?: string; private?: boolean }) =>
    post<{ repo: GitHubRepo }>('/github/repos', {
      name,
      org: options?.org || 'lumeniqjs',
      description: options?.description || '',
      private: options?.private ?? true,
    }).then(r => r.repo),

  // ─── Project Management ─────────────────────────────────────────

  /** DELETE /api/projects/:id */
  removeProject: async (projectId: string) => {
    const auth = await authHeaders();
    const res = await fetch(`${API_BASE}/api/projects/${projectId}`, { method: 'DELETE', headers: auth });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: res.statusText }));
      const error = new Error(data.message || data.error || `API error: ${res.status}`) as Error & { status?: number };
      error.status = res.status;
      throw error;
    }
    return res.json() as Promise<{ success: boolean; removed: string }>;
  },

  // ─── Logs ──────────────────────────────────────────────────────

  /** GET /api/logs/runs?project_id=X&limit=N */
  logRuns: (projectId?: string, limit = 50) => {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    params.set('limit', String(limit));
    return get<{ runs: PipelineRun[] }>(`/logs/runs?${params}`).then(r => r.runs).catch(() => []);
  },

  /** GET /api/logs/runs/:id */
  logRunDetail: (id: string) =>
    get<{ run: RunDetail }>(`/logs/runs/${id}`).then(r => r.run).catch(() => null),
  // ─── Infrastructure ─────────────────────────────────────────
  /** GET /api/infrastructure/status */
  getInfraStatus: () =>
    get<InfraStatusResponse>('/infrastructure/status'),

  /** GET /api/infrastructure/:service — per-service detail */
  getInfraDetail: (service: string) =>
    get<InfraDetailResponse>(`/infrastructure/${service}`),

  // ─── Safety Classifier ──────────────────────────────────────

  /** POST /api/safety/classify */
  safetyClassify: (request: string, accountId?: string, projectId?: string) =>
    post<SafetyResult>('/safety/classify', { request, account_id: accountId, project_id: projectId }),

  /** GET /api/safety/logs */
  safetyLogs: (projectId?: string, limit = 50) => {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    params.set('limit', String(limit));
    return get<{ logs: SafetyLog[] }>(`/safety/logs?${params}`).then(r => r.logs).catch(() => []);
  },

  /** GET /api/safety/categories */
  safetyCategories: () =>
    get<SafetyCategories>('/safety/categories'),

  // ─── Credentials (BYOK) ─────────────────────────────────────

  /** GET /api/credentials/workspace */
  getWorkspaceCredentials: (accountId: string) =>
    get<{ credentials: WorkspaceCredential[] }>(`/credentials/workspace?account_id=${accountId}`).then(r => r.credentials),

  /** POST /api/credentials/workspace */
  saveWorkspaceCredential: (accountId: string, service: string, value: string, oauthRefresh?: string) =>
    post<{ id: string; status: string }>('/credentials/workspace', { account_id: accountId, service, value, oauth_refresh: oauthRefresh }),

  /** POST /api/credentials/workspace/:id/test */
  testWorkspaceCredential: (id: string) =>
    post<{ id: string; status: string; tested_at: string }>(`/credentials/workspace/${id}/test`),

  /** DELETE /api/credentials/workspace/:id */
  deleteWorkspaceCredential: (id: string) =>
    del<{ id: string; status: string }>(`/credentials/workspace/${id}`),

  /** POST /api/credentials/survey/submit — canonical sealed-box intake */
  submitSurveyCredential: (projectId: string, service: string, keyName: string, value: string) =>
    post<{ masked_value: string; validation_status: string }>('/credentials/survey/submit', { project_id: projectId, service, key_name: keyName, value }),

  /** GET /api/credentials/project/:projectId */
  getProjectCredentials: (projectId: string) =>
    get<{ credentials: ProjectCredential[] }>(`/credentials/project/${projectId}`).then(r => r.credentials),

  /** POST /api/credentials/project */
  saveProjectCredential: (projectId: string, service: string, keyName: string, value: string) =>
    post<{ id: string; masked_value: string; validation_status: string; validation_error?: string; status: string }>('/credentials/project', { project_id: projectId, service, key_name: keyName, value }),

  /** POST /api/credentials/project/:id/test */
  testProjectCredential: (id: string) =>
    post<{ id: string; validation_status: string; validation_error?: string; tested_at: string }>(`/credentials/project/${id}/test`),

  /** DELETE /api/credentials/project/:id — soft revoke */
  deleteProjectCredential: (id: string) =>
    del<{ id: string; status: string }>(`/credentials/project/${id}`),

  /** GET /api/credentials/requirements/:moduleSlug */
  getCredentialRequirements: (moduleSlug: string, projectId?: string) =>
    get<{ module: string; requirements: Array<{ service: string; key_name: string; label: string; required: boolean }>; unmet?: Array<{ service: string; key_name: string; label: string; required: boolean }>; all_met?: boolean }>(`/credentials/requirements/${moduleSlug}${projectId ? `?project_id=${projectId}` : ''}`),

  /** POST /api/credentials/env/:projectId — download .env file */
  downloadEnv: (projectId: string, accountId: string) =>
    authHeaders().then(auth => fetch(`${API_BASE}/api/credentials/env/${projectId}?account_id=${accountId}`, { method: 'POST', headers: auth })),

  /** GET /api/credentials/status */
  getCredentialStatus: (accountId: string) =>
    get<{ services: CredentialServiceStatus[] }>(`/credentials/status?account_id=${accountId}`).then(r => r.services),

  /** POST /api/credentials/workspace with key_name for multi-key services (AC-1) */
  saveWorkspaceCredentialKeyed: (accountId: string, service: string, keyName: string, value: string) =>
    post<{ id: string; status: string }>('/credentials/workspace', { account_id: accountId, service, key_name: keyName, value }),

  /** POST /api/credentials/validate — re-validate a provider credential */
  validateProvider: (provider: string, credentials: Record<string, string>) =>
    post<{ provider: string; valid: boolean; checks: Array<{ name: string; passed: boolean; message: string }>; errors: string[]; warnings: string[] }>('/credentials/validate', { provider, credentials }),

  /** GET /api/credentials/services/:accountId/:platform — list services/sites from stored token (AC-17/18) */
  listProviderServices: (accountId: string, platform: string) =>
    get<{ platform: string; services?: Array<{ id: string; name: string; type?: string; project?: string }>; sites?: Array<{ id: string; name: string; url: string }>; projects?: Array<{ id: string; name: string; url?: string; services?: Array<{ id: string; name: string }> }> }>(`/credentials/services/${accountId}/${platform}`),

  /** PATCH /api/credentials/platforms/:id — save platform selections (AC-9b) */
  savePlatformSelections: (subscriberId: string, selections: { platform_api_hosting?: string; platform_frontend?: string; platform_database?: string }) =>
    patch<{ status: string }>(`/credentials/platforms/${subscriberId}`, selections),

  // ─── Artifacts ──────────────────────────────────────────────

  /** GET /api/artifacts/:projectId */
  getArtifacts: (projectId: string, tab?: string) => {
    const params = tab ? `?tab=${tab}` : '';
    return get<{ artifacts: Artifact[] }>(`/artifacts/${projectId}${params}`).then(r => r.artifacts);
  },

  /** GET /api/artifacts/detail/:id */
  getArtifactDetail: (id: string) =>
    get<{ artifact: Artifact }>(`/artifacts/detail/${id}`).then(r => r.artifact),

  /** POST /api/artifacts/upload */
  uploadArtifact: (projectId: string, filename: string, content: string, mimeType: string) =>
    post<{ id: string; status: string }>('/artifacts/upload', { project_id: projectId, filename, content, mime_type: mimeType }),

  /** POST /api/artifacts/generated */
  saveGeneratedArtifact: (projectId: string, filename: string, content: string, repoPath?: string) =>
    post<{ id: string; status: string }>('/artifacts/generated', { project_id: projectId, filename, content, repo_path: repoPath }),

  /** PATCH /api/artifacts/:id */
  updateArtifact: (id: string, updates: { content?: string; display_name?: string }) =>
    patch<{ id: string; status: string }>(`/artifacts/${id}`, updates),

  /** POST /api/artifacts/:id/commit */
  commitArtifact: (id: string) =>
    post<ArtifactCommitResult>(`/artifacts/${id}/commit`),

  /** DELETE /api/artifacts/:id */
  removeArtifact: (id: string) =>
    del<{ id: string; status: string }>(`/artifacts/${id}`),

  /** GET /api/artifacts/context/:projectId */
  getContextArtifacts: (projectId: string) =>
    get<{ artifacts: Array<{ filename: string; content: string }>; loading_rules: Record<string, string[]> }>(`/artifacts/context/${projectId}`),

  /** Fetch key context docs directly from the project repo */
  getRepoDocs: (projectId: string) =>
    get<{ docs: Array<{ path: string; label: string; category: string; content: string; size: number }>; project_id: string; repo: string }>(`/artifacts/repo-docs/${projectId}`),

  /** Get version history for a generated doc */
  getArtifactVersions: (projectId: string, filename: string) =>
    get<{ versions: Array<{ id: string; version: number; content: string; created_at: string; updated_at: string }>; project_id: string; filename: string }>(`/artifacts/versions/${projectId}/${encodeURIComponent(filename)}`),

  // ─── Skills Library ────────────────────────────────────────
  listSkills: (params?: string) =>
    get<{ skills: any[]; total: number }>(`/skills${params ? `?${params}` : ''}`),

  // ─── Wizard ────────────────────────────────────────────────
  wizardStart: (data: { name: string; description?: string; techStack?: string; deploymentTarget?: string; domain?: string; repo_owner?: string; repo_name?: string; default_branch?: string; project_type?: string; wizard_path?: string[] }) =>
    post<{ projectId: string; wizardSessionId: string; dnsStatus: WizardDnsStatus }>('/bootstrap/wizard/start', data),
  wizardValidate: (data: { wizardSessionId: string; service: string; [key: string]: unknown }) =>
    post<WizardValidateResult>('/bootstrap/wizard/validate', data),
  wizardKickoff: (data: { wizardSessionId: string; projectId: string }) =>
    post<{ runId: string; streamUrl: string }>('/bootstrap/wizard/kickoff', data),
  wizardDnsStatus: (wizardSessionId: string) =>
    get<WizardDnsStatus>(`/bootstrap/wizard/dns-status/${wizardSessionId}`),
  wizardStatus: (wizardSessionId: string) =>
    get<WizardStatusResponse>(`/bootstrap/wizard/status/${wizardSessionId}`),
  wizardResume: (projectId: string) =>
    get<WizardResumeResponse>(`/bootstrap/wizard/resume/${projectId}`),
  wizardClassify: (description: string) =>
    post<{ suggestedType: string; services: string[]; reasoning: string }>('/wizard/classify', { description }),
  wizardPreValidate: (service: string, credentials: Record<string, string>) =>
    post<PreValidateResult>('/bootstrap/wizard/pre-validate', { service, credentials }),
  wizardPersistStep: (wizardSessionId: string, step: number) =>
    patch<{ ok: boolean }>('/bootstrap/wizard/step', { wizardSessionId, step }),
  wizardGenerateSpec: (wizardSessionId: string, answers: {
    problemStatement: string;
    targetUsers: string;
    coreFeatures: string;
    techPreferences: string;
    successCriteria: string;
  }): EventSource => {
    // Returns an EventSource-like object for SSE streaming
    // We use fetch + ReadableStream since EventSource doesn't support POST
    const source = new EventTarget() as EventTarget & { close: () => void; fullText: string };
    source.fullText = '';
    const controller = new AbortController();
    source.close = () => controller.abort();
    authHeaders().then(auth => fetch(`${API_BASE}/api/bootstrap/wizard/generate-spec`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify({ wizardSessionId, answers }),
      signal: controller.signal,
    })).then(async (res) => {
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        source.dispatchEvent(new CustomEvent('error', { detail: err }));
        return;
      }
      const reader = res.body?.getReader();
      if (!reader) return;
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();
            try {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                source.fullText += parsed.text;
                source.dispatchEvent(new CustomEvent('delta', { detail: parsed }));
              }
              if (parsed.specMd) {
                source.dispatchEvent(new CustomEvent('complete', { detail: parsed }));
              }
              if (parsed.error) {
                source.dispatchEvent(new CustomEvent('error', { detail: parsed }));
              }
            } catch { /* skip non-JSON lines */ }
          }
        }
      }
    }).catch((err: Error) => {
      if (err.name !== 'AbortError') {
        source.dispatchEvent(new CustomEvent('error', { detail: { error: err.message } }));
      }
    });
    return source as unknown as EventSource;
  },
  wizardSaveSpec: (wizardSessionId: string, specMd: string) =>
    post<{ ok: boolean }>('/bootstrap/wizard/save-spec', { wizardSessionId, specMd }),
  wizardParseSpec: async (file: File): Promise<ParsedSpec> => {
    const formData = new FormData();
    formData.append('file', file);
    const auth = await authHeaders();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60_000); // 60s timeout
    try {
      const res = await fetch(`${API_BASE}/api/bootstrap/wizard/parse-spec`, {
        method: 'POST',
        headers: auth,
        body: formData,
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || 'Failed to parse spec');
      }
      return res.json();
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new Error('Spec analysis timed out after 60 seconds. Please try a smaller document or try again.');
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }
  },

  // ─── Repair Endpoint (Task 6) ─────────────────────────────────
  async repairProject(projectId: string, action: 'retry' | 'reset' = 'retry'): Promise<{
    success: boolean;
    diagnosis?: {
      projectId: string;
      stateBefore: string;
      stateAfter: string;
      repaired: boolean;
      diagnosis: string;
      actions: string[];
      nextStep: 'connect_repo' | 'sync' | 'ready' | 'reset';
    };
    error?: string;
  }> {
    const auth = await authHeaders();
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/repair`, {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    });
    return res.json();
  },

  /** POST /api/projects/:id/verify — create a test issue to verify the pipeline */
  async verifyProject(projectId: string): Promise<{
    success: boolean;
    issue_number?: number;
    issue_url?: string;
    message?: string;
    error?: string;
  }> {
    const auth = await authHeaders();
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/verify`, {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
    });
    return res.json();
  },

  /** GET /api/projects/:id/verify/status?issue=N — poll for pipeline run status */
  async verifyStatus(projectId: string, issueNumber: number): Promise<{
    status: string;
    run_id?: string;
    pr_number?: number | null;
    branch_name?: string | null;
    tasks_completed?: number;
    tasks_total?: number;
    issue_closed?: boolean;
    message?: string;
    error?: string;
  }> {
    const auth = await authHeaders();
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/verify/status?issue=${issueNumber}`, {
      headers: auth,
    });
    return res.json();
  },

  // ─── Usage / Token Balance ──────────────────────────────────
  getUsage: () =>
    get<UsageSummary>('/billing/usage'),

  /** POST /api/v2/chat/cancel-merge — cancel a pending medium-risk merge */
  chatV2CancelMerge: (sessionId: string, pullNumber: number) =>
    post<{ status: string; pull_number: number }>('/v2/chat/cancel-merge', { session_id: sessionId, pull_number: pullNumber }),

  /** POST /api/v2/chat/confirm-scope — confirm batch pipeline execution */
  chatV2ConfirmScope: (sessionId: string) =>
    post<{ status: string }>('/v2/chat/confirm-scope', { session_id: sessionId }),

  /** POST /api/v2/chat/cancel-scope — cancel batch pipeline execution */
  chatV2CancelScope: (sessionId: string) =>
    post<{ status: string }>('/v2/chat/cancel-scope', { session_id: sessionId }),

  /** POST /api/v2/chat/confirm-merge — confirm a pending medium-risk merge */
  chatV2ConfirmMerge: (sessionId: string, pullNumber: number) =>
    post<{ status: string; pull_number: number; sha?: string }>('/v2/chat/confirm-merge', { session_id: sessionId, pull_number: pullNumber }),

  /** POST /api/runs/cancel-all — cancel all active runs for a project */
  cancelAllRuns: (projectId: string) =>
    post<{ cancelled: number; project_id: string }>('/runs/cancel-all', { project_id: projectId }),

  // ─── Chat V2 (Conversational) ──────────────────────────────

  /** POST /api/v2/chat/message — send a message, get streaming response */
  chatV2Send: (message: string, projectId: string, sessionId?: string) =>
    post<ChatV2SendResponse>('/v2/chat/message', { message, project: projectId, session_id: sessionId }),

  /** POST /api/v2/chat/message — send a message with image attachment */
  chatV2SendWithImage: (message: string, projectId: string, image: { data: string; media_type: string; filename?: string }, sessionId?: string) =>
    post<ChatV2SendResponse>('/v2/chat/message', { message, project: projectId, session_id: sessionId, image }),

  /** GET /api/v2/chat/sessions?project=xxx */
  chatV2Sessions: (projectId: string) =>
    get<{ sessions: ChatV2Session[] }>(`/v2/chat/sessions?project=${encodeURIComponent(projectId)}`).then(r => r.sessions).catch(() => []),

  /** GET /api/v2/chat/sessions/:id/messages */
  chatV2Messages: (sessionId: string) =>
    get<{ messages: ChatV2Message[] }>(`/v2/chat/sessions/${sessionId}/messages`).then(r => r.messages).catch(() => []),

  /** DELETE /api/v2/chat/sessions/:id */
  chatV2DeleteSession: (sessionId: string) =>
    del<{ status: string; deleted: string }>(`/v2/chat/sessions/${sessionId}`),

  /** POST /api/v2/chat/escalate — start pipeline from conversation */
  chatV2Escalate: (sessionId: string, projectId: string, brief: string) =>
    post<{ message_id: string; session_id: string; type: string }>('/v2/chat/escalate', { session_id: sessionId, project: projectId, brief }),

  /** POST /api/v2/chat/fix-finding — directly fix a single finding via pipeline */
  chatV2FixFinding: (projectId: string, findingId: string, sessionId?: string) =>
    post<{ message_id: string; session_id: string; finding_id: string; type: string }>('/v2/chat/fix-finding', { project: projectId, finding_id: findingId, session_id: sessionId }),

  /** POST /api/v2/chat/fix-all — fix all unresolved findings for a project */
  chatV2FixAll: (projectId: string, sessionId?: string, maxFindings?: number) =>
    post<{ session_id: string; total_findings: number; findings: Array<{ id: string; title: string; severity: string }>; type: string }>('/v2/chat/fix-all', { project: projectId, session_id: sessionId, max_findings: maxFindings }),

  /** GET /api/v2/chat/context/:projectId — get project context summary */
  chatV2Context: (projectId: string) =>
    get<ChatV2ContextResponse>(`/v2/chat/context/${projectId}`),

  /** POST /api/v2/chat/session/handoff — summarize current session and create a new one */
  chatV2Handoff: (sessionId: string, projectId: string) =>
    post<ChatV2HandoffResponse>('/v2/chat/session/handoff', { session_id: sessionId, project: projectId }),

  /** GET /api/v2/chat/freshness/:projectId — check if cached context is stale */
  chatV2Freshness: (projectId: string) =>
    get<ChatV2FreshnessResult>(`/v2/chat/freshness/${projectId}`),

  /** POST /api/v2/chat/refresh/:projectId — force-refresh context from GitHub */
  chatV2Refresh: (projectId: string) =>
    post<{ refreshed: boolean; project_id: string; total_context_tokens: number; loaded_at: string }>(`/v2/chat/refresh/${projectId}`, {}),

  /** Build SSE URL for streaming chat tokens */
  chatV2StreamUrl: (sessionId: string) =>
    `${API_BASE}/api/v2/chat/stream/${sessionId}`,

  // ─── Agent Chat (Operator — S50-P1/P3) ────────────────────

  /** POST /api/v2/agent/message — start or continue an agent session */
  agentSend: (message: string, sessionId?: string) =>
    post<{ message_id: string; session_id: string; type: string }>('/v2/agent/message', { message, session_id: sessionId }),

  /** GET /api/v2/agent/sessions — list agent sessions */
  agentSessions: () =>
    get<{ sessions: ChatSession[] }>('/v2/agent/sessions').then(r => r.sessions).catch(() => []),

  /** GET /api/v2/agent/session/:sessionId/messages — load session history */
  agentMessages: (sessionId: string) =>
    get<{ messages: ChatMessage[] }>(`/v2/agent/session/${sessionId}/messages`).then(r => r.messages).catch(() => []),

  /** POST /api/v2/agent/session/:sessionId/approve — approve or reject a pending tool call */
  agentApprove: (sessionId: string, toolUseId: string, toolName: string, toolInput: Record<string, unknown>, approved: boolean, reason?: string) =>
    post<{ status: string; session_id: string }>(`/v2/agent/session/${sessionId}/approve`, {
      tool_use_id: toolUseId, tool_name: toolName, tool_input: toolInput, approved, reason,
    }),

  /** PATCH /api/v2/agent/sessions/:id — update cost cap for a session */
  agentUpdateCap: (sessionId: string, costCapUsd: number) =>
    patch<{ ok: boolean; cost_cap_usd: number; status: string }>(`/v2/agent/sessions/${sessionId}`, { cost_cap_usd: costCapUsd }),

  /** Build SSE URL for agent session streaming */
  agentStreamUrl: (sessionId: string) =>
    `${API_BASE}/api/v2/agent/stream/${sessionId}`,

  /** GET /api/v2/agent/sessions/search?q=<query>&limit=<n> — FTS session search */
  agentSessionSearch: (q: string, limit = 10) =>
    get<{ sessions: AgentSessionSearchResult[] }>(
      `/v2/agent/sessions/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    ).then((r) => r.sessions).catch(() => []),

  // ─── Issue Quality Gate ────────────────────────────────────
  /** GET /api/projects/:id/quality-gate */
  qualityGate: (projectId: string) =>
    get<QualityGateSettings>(`/projects/${projectId}/quality-gate`),

  /** PUT /api/projects/:id/quality-gate */
  qualityGateUpdate: (projectId: string, settings: { issue_quality_gate_enabled?: boolean; issue_quality_threshold?: number }) =>
    put<{ status: string }>(`/projects/${projectId}/quality-gate`, settings),

  // ─── Manus ─────────────────────────────────────────────────
  /** POST /api/manus/ingest-report — manual recovery for missed webhooks */
  manusIngestReport: (taskId: string, projectId: string, reportMarkdown: string) =>
    post<{ p0: number; p1: number; p2: number; github_issues_created: number }>('/manus/ingest-report', { task_id: taskId, project_id: projectId, report_markdown: reportMarkdown }),

  /** GET /api/manus/reviews/:projectId — list Manus deep reviews */
  manusReviews: (projectId: string, limit = 20) =>
    get<{ reviews: ManusReview[] }>(`/manus/reviews/${projectId}?limit=${limit}`).then(r => r.reviews),

  /** GET /api/manus/cadence/:projectId — get cadence settings */
  manusCadence: (projectId: string) =>
    get<CadenceSettings>(`/manus/cadence/${projectId}`),

  /** PUT /api/manus/cadence/:projectId — update cadence settings */
  manusCadenceUpdate: (projectId: string, settings: { review_cadence_enabled?: boolean; review_cadence_weeks?: number }) =>
    put<{ status: string }>(`/manus/cadence/${projectId}`, settings),

  // ─── Modules ──────────────────────────────────────────────────

  /** GET /api/modules — list all modules */
  listModules: (opts?: { category?: string; status?: string }) =>
    get<{ modules: ModuleRegistryEntry[] }>(`/modules${opts?.category ? `?category=${opts.category}` : ''}${opts?.status ? `${opts?.category ? '&' : '?'}status=${opts.status}` : ''}`).then(r => r.modules),

  /** GET /api/modules/:slug — module detail */
  getModule: (slug: string) =>
    get<{ module: ModuleRegistryEntry }>(`/modules/${slug}`).then(r => r.module),

  /** GET /api/projects/:id/modules — installed modules for project */
  getProjectModules: (projectId: string) =>
    get<{ modules: InstalledModule[] }>(`/projects/${projectId}/modules`).then(r => r.modules),

  /** POST /api/projects/:id/modules — add module */
  addProjectModule: (projectId: string, slug: string) =>
    post<{ status: string; module: ModuleRegistryEntry; dependencies_added: string[]; all_added: string[] }>(`/projects/${projectId}/modules`, { slug }),

  /** DELETE /api/projects/:id/modules/:moduleId — remove module */
  removeProjectModule: (projectId: string, moduleId: string) =>
    del<{ status: string }>(`/projects/${projectId}/modules/${moduleId}`),
};

// ─── Quality Gate Types ──────────────────────────────────────
export interface QualityGateSettings {
  issue_quality_gate_enabled: boolean;
  issue_quality_threshold: number;
  recent_avg_score: number | null;
  recent_override_count: number;
  recent_score_count: number;
}

// ─── Manus Review Types ──────────────────────────────────────
export interface ManusReview {
  id: string;
  project_id: string;
  provider: string;
  review_engine: string;
  review_depth: string;
  status: string;
  review_score: string | null;
  review_layers: unknown;
  findings_json: unknown;
  architecture_summary: string | null;
  tech_debt_score: number | null;
  risk_areas_json: unknown;
  p0_count: number | null;
  p1_count: number | null;
  p2_count: number | null;
  tokens_used: number | null;
  cost_usd: number | null;
  duration_ms: number | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface CadenceSettings {
  review_cadence_enabled: boolean;
  review_cadence_weeks: number;
  review_last_scheduled_at: string | null;
  review_next_scheduled_at: string | null;
}

// ─── Module Types ─────────────────────────────────────────────
export interface ModuleRegistryEntry {
  id: string;
  name: string;
  slug: string;
  display_name: string;
  description: string;
  category: string;
  tier: string;
  dependencies: string[];
  source_product?: string;
  status: string;
  config_schema?: Record<string, unknown>;
  icon?: string;
  docs_url?: string;
  trigger_phrases?: string[];
  credential_requirements?: Array<{ service: string; key_name: string; label: string; required: boolean }>;
  config_options?: Array<{ key: string; label: string; type: string; default?: unknown; options?: string[]; placeholder?: string }>;
}

export interface InstalledModule {
  id: string;
  project_id: string;
  module_id: string;
  config: Record<string, unknown>;
  installed_at: string;
  module: ModuleRegistryEntry;
}

// ─── Usage Types ──────────────────────────────────────────────
export interface UsageSummary {
  subscriberId: string;
  plan: string;
  status: string;
  creditsUsed: number;
  creditsRemaining: number;
  creditCap: number;
  usagePercent: number;
  warningLevel: 'none' | 'low' | 'empty';
  creditSource: 'mcp_wallet';
  resetAt: string | null;
  recentRuns: Array<{
    id: string;
    projectId: string;
    status: string;
    creditsUsed: number;
    totalCostUsd: number;
    startedAt: string;
  }>;
  byModel: Record<string, { calls: number; credits: number; costUsd: number }>;
  byPhase: Record<string, { inputTokens: number; outputTokens: number; costUsd: number }>;
  byProject: Record<string, { runs: number; chatMessages: number; creditsUsed: number; totalCostUsd: number }>;
}

export interface ParsedSpec {
  parsed: {
    name: string | null;
    displayName: string | null;
    description: string | null;
    techStack: string | null;
    deploymentTarget: string | null;
    domain: string | null;
    services: Record<string, boolean>;
    keyFeatures: string[];
    summary: string | null;
    problemStatement: string | null;
    targetUsers: string | null;
    coreFeatures: string | null;
    techPreferences: string | null;
    successCriteria: string | null;
  };
  filename: string;
  extractedChars: number;
}

export interface InfraService {
  id: string;
  name: string;
  status: 'healthy' | 'degraded' | 'unreachable';
  latency_ms: number;
  detail: Record<string, string>;
}

export interface InfraStatusResponse {
  overall: 'healthy' | 'degraded';
  checked_at: string;
  services: InfraService[];
}

export interface InfraDetailResponse {
  service: string;
  checked_at?: string;
  error?: string;
  detail: Record<string, unknown>;
}

// ─── Safety Types ───────────────────────────────────────────────

export interface SafetyResult {
  safe: boolean;
  category: string;
  reason: string;
  gate_required: boolean;
  flagged_for_review: boolean;
  decline_message?: string;
  gate_message?: string;
}

export interface SafetyLog {
  id: string;
  account_id: string | null;
  project_id: string | null;
  category: string;
  request_hash: string;
  blocked: boolean;
  reason: string | null;
  created_at: string;
}

export interface SafetyCategories {
  prohibited: Array<{ id: string; decline_message: string }>;
  restricted: Array<{ id: string; gate_message: string }>;
}

// ─── Credential Types ───────────────────────────────────────────

export interface WorkspaceCredential {
  id: string;
  service: string;
  has_oauth_refresh: boolean;
  tested_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectCredential {
  id: string;
  service: string;
  key_name: string;
  masked_value?: string;
  status: string;
  validation_status: string;
  validated_at: string | null;
  used_by?: string[];
  tested_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CredentialServiceStatus {
  service: string;
  connected: boolean;
  tested_at: string | null;
}

// ─── Artifact Types ─────────────────────────────────────────────

export interface Artifact {
  id: string;
  tab: 'input' | 'generated';
  filename: string;
  display_name: string | null;
  mime_type: string;
  file_size: number | null;
  content?: string;
  repo_path: string | null;
  version: number;
  committed: boolean;
  committed_at: string | null;
  commit_sha: string | null;
  pr_number: number | null;
  has_content?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ArtifactCommitResult {
  id: string;
  status: 'committed' | 'already_up_to_date';
  pr_number?: number;
  pr_url?: string;
  branch?: string;
  action?: 'add' | 'update';
  auto_mergeable?: boolean;
}

// ─── Journal ─────────────────────────────────────────────────────

export interface JournalEntry {
  id: string;
  project_id: string;
  run_id: string | null;
  event_type: string;
  pr_number: number | null;
  pr_url: string | null;
  issue_number: number | null;
  issue_title: string | null;
  summary: string;
  root_cause: string | null;
  approach: string | null;
  alternatives: string | null;
  files_touched: string[] | null;
  areas: string[] | null;
  outcome: string | null;
  lessons: string | null;
  tokens_used: number | null;
  cost_usd: string | null;
  detail_json: Record<string, unknown> | null;
  created_at: string;
}

const journal = {
  list: (projectId: string, limit = 20) =>
    get<JournalEntry[]>(`/projects/${projectId}/journal?limit=${limit}`),
  get: (projectId: string, entryId: string) =>
    get<JournalEntry>(`/projects/${projectId}/journal/${entryId}`),
};

/// Attach journal to api object
(api as any).journal = journal;

// ─── Guardian KPIs ───────────────────────────────────────────────
export interface GuardianKpi {
  id: string;
  guardian_run_id: string;
  project_id: string;
  pr_number: number | null;
  line_coverage: string | null;
  branch_coverage: string | null;
  function_coverage: string | null;
  new_code_coverage: string | null;
  pass_rate: string | null;
  total_tests: number | null;
  tests_unit: number | null;
  tests_integration: number | null;
  tests_e2e: number | null;
  flaky_test_count: number | null;
  flaky_tests: Array<{ test_name: string; fail_count: number; last_failed: string }> | null;
  regression_rate: string | null;
  execution_time_ms: number | null;
  line_coverage_delta: string | null;
  execution_time_delta: string | null;
  created_at: string;
}

export interface CoverageThresholds {
  line: number;
  branch: number;
  function: number;
}

const guardianKpis = {
  list: (projectId: string, limit = 20) =>
    get<{ kpis: GuardianKpi[] }>(`/guardian/kpis/${projectId}?limit=${limit}`).then(r => r.kpis),
  latest: (projectId: string) =>
    get<{ kpi: GuardianKpi | null }>(`/guardian/kpis/${projectId}/latest`).then(r => r.kpi),
  thresholds: (projectId: string) =>
    get<{ thresholds: CoverageThresholds }>(`/guardian/thresholds/${projectId}`).then(r => r.thresholds),
  updateThresholds: (projectId: string, thresholds: Partial<CoverageThresholds>) =>
    put<{ status: string }>(`/guardian/thresholds/${projectId}`, thresholds),
  runs: (projectId: string, limit = 20) =>
    get<{ runs: GuardianRunRecord[] }>(`/guardian/runs/${projectId}?limit=${limit}`).then(r => r.runs),
};
(api as any).guardianKpis = guardianKpis;

// ─── Sync Findings ──────────────────────────────────────────

export interface SyncFinding {
  id: string;
  project_id: string;
  sync_run_id?: string;
  category: string;
  severity: string;
  title: string;
  description: string;
  suggestion?: string;
  file_path?: string;
  auto_fixable: boolean;
  review_engine?: 'manus' | 'gemini_opus';
  resolved: boolean;
  resolved_at?: string;
  created_at: string;
}

export interface FindingsSummary {
  total: number;
  unresolved: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  byCategory: Record<string, number>;
}

export interface AggregateFindings {
  totalProjects: number;
  totalOpenFindings: number;
  patterns: Array<{
    findingType: string;
    category: string;
    affectedProjects: number;
    totalProjects: number;
    percentage: number;
    severity: string;
    message: string;
    /** Affected project ids (older API builds omit it). */
    projectIds?: string[];
  }>;
  resolutionRate: {
    last30Days: { opened: number; resolved: number; rate: number };
  };
}

const findings = {
  list: (projectId: string, opts?: { unresolved?: boolean; limit?: number }) =>
    get<{ findings: SyncFinding[] }>(
      `/findings/${projectId}?limit=${opts?.limit ?? 50}${opts?.unresolved ? '&unresolved=true' : ''}`
    ).then(r => r.findings),
  summary: (projectId: string) =>
    get<FindingsSummary>(`/findings/${projectId}/summary`),
  byRun: (syncRunId: string) =>
    get<{ findings: SyncFinding[] }>(`/findings/run/${syncRunId}`).then(r => r.findings),
  resolve: (findingId: string) =>
    post<{ status: string }>(`/findings/${findingId}/resolve`, {}),
  exportFindings: (projectId: string, format: 'pdf' | 'markdown' | 'gdocs', filter?: { engine?: string; priority?: string; status?: string }) =>
    post<{ format: string; content: string; count: number }>(`/findings/${projectId}/export`, { format, filter }),
  aggregate: (subscriberId: string) =>
    get<AggregateFindings>(`/findings/aggregate?subscriberId=${subscriberId}`),
};
(api as any).findings = findings;
(api as any).get = get;
(api as any).post = post;

// ─── Issues API ──────────────────────────────────────────

export interface ProjectIssue {
  id: string;
  project_id: string;
  finding_id: string | null;
  review_engine: string | null;
  priority: string;
  title: string;
  description: string;
  file_path: string | null;
  line_number: number | null;
  remediation: string | null;
  effort_estimate: string | null;
  tag: string | null;
  source: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

const issues = {
  list: (projectId: string, status?: string) =>
    get<{ issues: ProjectIssue[] }>(
      `/issues/${projectId}${status ? `?status=${status}` : ''}`
    ).then(r => r.issues),
  updateStatus: (issueId: string, status: string) =>
    patch<{ status: string }>(`/issues/${issueId}/status`, { status }),
};
(api as any).issues = issues;

// ─── Wiki Vault ───────────────────────────────────────────

export interface WikiPage {
  id: string;
  entity_type: string;
  entity_name: string;
  slug: string;
  status: string;
  summary: string | null;
  content_md?: string;
  linked_entity_slugs?: string[];
  raw_source_ids?: string[];
  version: number;
  last_ingest_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WikiSearchResult {
  id: string;
  entity_type: string;
  entity_name: string;
  slug: string;
  status: string;
  summary: string | null;
  similarity: number | null;
}

export interface WikiHealth {
  orphaned_entities: number;
  stale_entities: number;
  total_conflicts: number;
  total_pages: number;
}

export interface WikiIngestLogEntry {
  id: string;
  raw_inputs_processed: number;
  entities_created: number;
  entities_updated: number;
  conflicts_detected: number;
  orphaned_entities: number;
  duration_ms: number | null;
  triggered_by: string;
  created_at: string;
}

const wiki = {
  pages: (projectId: string, opts?: { entity_type?: string; status?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.entity_type) params.set('entity_type', opts.entity_type);
    if (opts?.status) params.set('status', opts.status);
    if (opts?.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return get<{ pages: WikiPage[] }>(`/wiki/${projectId}/pages${qs ? `?${qs}` : ''}`).then(r => r.pages);
  },
  page: (projectId: string, slug: string) =>
    get<{ page: WikiPage }>(`/wiki/${projectId}/pages/${slug}`).then(r => r.page),
  search: (projectId: string, query: string, limit = 10) =>
    get<{ results: WikiSearchResult[] }>(`/wiki/${projectId}/search?q=${encodeURIComponent(query)}&limit=${limit}`).then(r => r.results),
  health: (projectId: string) =>
    get<WikiHealth>(`/wiki/${projectId}/health`),
  log: (projectId: string, limit = 20) =>
    get<{ logs: WikiIngestLogEntry[] }>(`/wiki/${projectId}/log?limit=${limit}`).then(r => r.logs),
  ingest: (projectId: string, sourceType: string, content: string, sourceRef?: string) =>
    post<{ queued: boolean; ingest_started: boolean }>(`/wiki/${projectId}/ingest`, {
      source_type: sourceType,
      source_ref: sourceRef,
      content,
    }),
};
(api as any).wiki = wiki;

// ─── Wizard Types ──────────────────────────────────────────

export interface WizardDnsStatus {
  onCloudflare: boolean;
  propagated: boolean;
  nameservers: string[];
}

export interface DnsRecord {
  type: string;
  name: string;
  content: string;
  priority?: number;
  proxied?: boolean;
  written?: boolean;
  purpose?: string;
}

export interface WizardValidateResult {
  valid: boolean;
  error?: string;
  identity?: string;
  avatar?: string;
  automationLog: string[];
  dnsRecords?: DnsRecord[];
  /** URL of the created/existing GitHub repo */
  repoUrl?: string;
  /** Human-readable hint for what to do next */
  nextStepHint?: string;
  /** Machine-readable next step ID */
  nextStep?: string;
}

export interface WizardStatusResponse {
  steps: Record<string, 'pending' | 'complete' | 'failed' | 'skipped'>;
  dnsStatus: WizardDnsStatus;
  automationLog: string[];
  codexRunId: string | null;
  codexStatus: 'not_started' | 'running' | 'complete' | 'failed';
  unlockedCapabilities: string[];
}

export interface WizardResumeResponse {
  completed: boolean;
  projectId: string;
  wizardSessionId?: string;
  wizardStep?: number;
  displayName?: string;
  domain?: string;
  steps?: Record<string, 'pending' | 'complete' | 'failed' | 'skipped'>;
  dnsStatus?: WizardDnsStatus;
  automationLog?: string[];
  codexRunId?: string | null;
  codexStatus?: 'not_started' | 'running' | 'complete' | 'failed';
  unlockedCapabilities?: string[];
  error?: string;
}

export interface PreValidateCheck {
  name: string;
  passed: boolean;
  severity: 'error' | 'warning' | 'info';
  message: string;
  suggestion?: string;
}

export interface PreValidateResult {
  provider: string;
  valid: boolean;
  checks: PreValidateCheck[];
  scopes?: string[];
  capabilities?: string[];
  warnings: string[];
  errors: string[];
  durationMs: number;
}

// ─── Chat V2 Types ──────────────────────────────────────────────

export interface ChatV2SendResponse {
  message_id: string;
  assistant_message_id: string;
  session_id: string;
  type: 'chat';
  blocked?: boolean;
  reason?: string;
}

export interface ChatV2Session {
  id: string;
  project_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChatV2Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  type: string;
  status: string;
  run_id?: string | null;
  pr_number?: number | null;
  pr_url?: string | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  cost_usd?: number | null;
  created_at: string;
}

export interface ChatV2HandoffResponse {
  new_session_id: string;
  old_session_id: string;
  summary_tokens: number;
  summary_preview: string;
}

export interface ChatV2ContextResponse {
  project_id: string;
  display_name: string;
  repo: string;
  has_context: boolean;
  has_agents: boolean;
  has_worldview: boolean;
  findings_summary: string | null;
  recent_runs: string | null;
  total_context_tokens: number;
}

export interface ChatV2FreshnessResult {
  stale: boolean;
  staleFiles: string[];
  cachedAt: string | null;
  ageMinutes: number;
}

// ─── Agent Session Search (ocn-S57-P0) ──────────────────────────

export interface AgentSessionSearchResult {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  rank: number;
  excerpt: string;
}

// ─── Onboarding Wizard v3 ────────────────────────────────────

export interface WizardModule {
  slug: string;
  name: string;
  time_minutes: number;
  credits: number;
  required: boolean;
  needs_credentials: boolean;
}

export interface WizardPlanResponse {
  project_id: string;
  summary: string;
  modules: WizardModule[];
  integrations: Array<{ name: string; service: string; description: string }>;
  phases: Array<{
    phase_number: number;
    title: string;
    steps: Array<{
      name: string; description: string; type: 'module' | 'pipeline';
      module_key?: string; complexity?: string; dependencies: string[];
    }>;
  }>;
  estimates: { time_minutes: number; credits: number; pipeline_runs: number };
  commercialization_type: string;
  scaffolding?: {
    context_md: string; architecture_md: string;
    data_model_md: string; build_plan_md: string;
  };
}

// Maps the OnboardingWizard UI's "use case" vocabulary (personal / commercial
// / platform) to the backend's commercialization_type enum. Exported for unit
// tests; not part of the public API surface.
export function _wizardUseCaseToCommercializationType(
  useCase: 'personal' | 'commercial' | 'platform' | null | undefined,
): 'personal' | 'commercial_single' | 'commercial_multi' {
  if (useCase === 'platform') return 'commercial_multi';
  if (useCase === 'commercial') return 'commercial_single';
  return 'personal';
}

// Response shape consumed by the OnboardingWizard component. Differs from the
// backend's WizardPlanResponse — flattens estimates, renames slug→id, projects
// stack onto a default.
export interface WizardGeneratePlanResponse {
  project_id: string;
  title: string;
  type: 'personal' | 'commercial' | 'platform';
  stack: string;
  modules: Array<{
    id: string;
    name: string;
    required: boolean;
    needs_credentials: boolean;
    credential_fields?: Array<{ key: string; label: string; placeholder?: string }>;
  }>;
  integrations: Array<{ id: string; name: string }>;
  estimated_minutes: number;
  estimated_credits: number;
}

const wizardV3 = {
  start: (data: { name: string; description?: string; commercialization_type?: string; use_case: 'new_idea' | 'has_spec' }) =>
    post<{ project_id: string; modules: WizardModule[]; stack: any; commercialization_type: string }>('/wizard/v3/start', data),

  plan: (data: { project_id: string; description: string; commercialization_type: string }) =>
    post<WizardPlanResponse>('/wizard/v3/plan', data),

  analyze: (data: { project_id: string; content: string; filename?: string; commercialization_type: string }) =>
    post<WizardPlanResponse & { spec_id: string }>('/wizard/v3/analyze', data),

  // Convenience wrapper called by the OnboardingWizard at step 3 ("Your Project
  // Plan"). Composes /wizard/v3/start (creates project, returns project_id)
  // with either /wizard/v3/plan (new-idea flow, has `description`) or
  // /wizard/v3/analyze (has-spec flow, has `spec_text`), then adapts the
  // backend response to the flattened shape the component expects. The
  // returned project_id MUST be carried through to launch().
  generatePlan: async (data: {
    name: string;
    use_case: 'personal' | 'commercial' | 'platform' | null;
    description?: string;
    spec_text?: string;
  }): Promise<WizardGeneratePlanResponse> => {
    const commercialization_type = _wizardUseCaseToCommercializationType(data.use_case);
    const backend_use_case: 'new_idea' | 'has_spec' = data.description ? 'new_idea' : 'has_spec';

    const startRes = await post<{
      project_id: string;
      modules: WizardModule[];
      stack: any;
      commercialization_type: string;
    }>('/wizard/v3/start', {
      name: data.name,
      description: data.description,
      commercialization_type,
      use_case: backend_use_case,
    });

    const planRes = backend_use_case === 'new_idea'
      ? await post<WizardPlanResponse>('/wizard/v3/plan', {
          project_id: startRes.project_id,
          description: data.description || '',
          commercialization_type,
        })
      : await post<WizardPlanResponse & { spec_id: string }>('/wizard/v3/analyze', {
          project_id: startRes.project_id,
          content: data.spec_text || '',
          commercialization_type,
        });

    return {
      project_id: planRes.project_id,
      title: data.name,
      type: (data.use_case || 'personal') as 'personal' | 'commercial' | 'platform',
      stack: 'Next.js 14 + TypeScript + Tailwind + Supabase',
      modules: (planRes.modules || []).map((m: WizardModule) => ({
        id: m.slug,
        name: m.name,
        required: m.required,
        needs_credentials: m.needs_credentials,
      })),
      integrations: (planRes.integrations || []).map((i: { name: string; service: string }) => ({
        id: i.service,
        name: i.name,
      })),
      estimated_minutes: planRes.estimates?.time_minutes ?? 0,
      estimated_credits: planRes.estimates?.credits ?? 0,
    };
  },

  // Local-only credential check. There is no backend `/wizard/v3/validate`
  // endpoint — real credential validation happens at module-install time
  // inside /wizard/v3/launch. This stub reports per-module "valid" iff every
  // declared field has a non-empty value, which matches what the Validate Keys
  // button's visible feedback intends to communicate.
  validateCredentials: async (
    credentials: Record<string, Record<string, string>>,
  ): Promise<Record<string, boolean>> => {
    const result: Record<string, boolean> = {};
    for (const [moduleId, fields] of Object.entries(credentials)) {
      const values = Object.values(fields);
      result[moduleId] = values.length > 0 && values.every((v) => typeof v === 'string' && v.trim().length > 0);
    }
    return result;
  },

  launch: (data: {
    project_id: string; modules: string[]; repo_name?: string;
    repo_visibility?: 'public' | 'private'; create_repo?: boolean;
    credentials?: Record<string, Record<string, string>>;
    scaffolding?: { context_md?: string; architecture_md?: string; data_model_md?: string; build_plan_md?: string };
  }) =>
    post<{ status: string; project_id: string; modules_installed: string[] }>('/wizard/v3/launch', data),

  getState: (projectId: string) =>
    get<any>(`/wizard/v3/${projectId}`),
};
(api as any).wizardV3 = wizardV3;

export interface SubscriberCostCaps { per_task_usd: number | null; hourly_usd: number | null; daily_usd: number | null; weekly_usd: number | null; monthly_usd: number | null; }
export interface SubscriberCostCapsResponse { caps: SubscriberCostCaps; spend: Partial<Record<'hourly_usd' | 'daily_usd' | 'weekly_usd' | 'monthly_usd', { usd: number; resetAt: string }>>; }
const subscriberCostCaps = {
  get: () => get<SubscriberCostCapsResponse>('/subscriber/cost-caps'),
  put: (caps: SubscriberCostCaps) => put<SubscriberCostCapsResponse>('/subscriber/cost-caps', caps),
};
(api as typeof api & { subscriberCostCaps?: typeof subscriberCostCaps }).subscriberCostCaps = subscriberCostCaps;

// ─── Type-safe augmented export ─────────────────────────────────

type ApiType = typeof api & {
  findings: typeof findings;
  issues: typeof issues;
  wiki: typeof wiki;
  wizardV3: typeof wizardV3;
  get: typeof get;
  put: typeof put;
  subscriberCostCaps: typeof subscriberCostCaps;
};

export default api as ApiType;

export interface McpAuthorizationWorkspace { membership_id: string; name: string }
export const getMcpAuthorizationWorkspaces = (id: string): Promise<{ client_name: string; workspaces: McpAuthorizationWorkspace[] }> =>
  get(`/v1/mcp-auth/requests/${encodeURIComponent(id)}/workspaces`);
export const createMcpAuthorization = (authorization_request_id: string, membership_id: string): Promise<{ transaction_id?: string; operator_approval_id?: string; agent_approval_id?: string }> =>
  post('/v1/mcp-auth/transactions', { authorization_request_id, membership_id });
export interface McpPurchasedWallet {
  membership_id: string; name: string; spendable_credits: number; reserved_credits: number;
  burned_credits: number; per_run_cap_credits: number;
}
export const getMcpPurchasedWallets = (): Promise<{ wallets: McpPurchasedWallet[] }> => get('/v1/mcp-billing/wallets');
export const purchaseMcpCredits = (request_id: string, membership_id: string, block: '25' | '50' | '100'): Promise<{ status: string; url?: string }> =>
  post('/v1/mcp-billing/purchases', { request_id, membership_id, block });
